/**
 * @fileoverview Select component - Atomic UI element
 * 
 * A flexible, accessible select component with variants and error states.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Select
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Select variant definitions using CVA
 */
// cva takes one base and one config; see the note in Input.tsx.
const selectVariants = cva(
  [
    "flex w-full rounded-md bg-white px-3 py-2 text-sm",
    "placeholder:text-gray-400",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      hasBorder: {
        true: "border",
        false: "border-0 shadow-none",
      },
      variant: {
        default: "border-gray-300 focus-visible:ring-primary-500",
        error: "border-red-500 focus-visible:ring-red-500",
        success: "border-green-500 focus-visible:ring-green-500",
      },
      size: {
        default: "h-10",
        sm: "h-9 text-sm",
        lg: "h-11 text-base",
      },
    },
    defaultVariants: {
      hasBorder: true,
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Select option interface
 */
export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

/**
 * Select component props
 */
export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size">,
    VariantProps<typeof selectVariants> {
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display
   */
  helperText?: string;
  /**
   * Options for the select
   */
  options?: SelectOption[];
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Show border (default: true)
   */
  hasBorder?: boolean;
}

/**
 * Select component
 * 
 * A flexible select component with error states and helper text.
 * Fully accessible with proper ARIA attributes.
 * 
 * @param {SelectProps} props - Select props
 * @returns {JSX.Element} Select element with optional error/helper text
 * 
 * @example
 * <Select options={[{value: '1', label: 'Option 1'}]} />
 * 
 * @example
 * <Select variant="error" error="This field is required" />
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant, size, error, helperText, options, placeholder, hasBorder, children, ...props }, ref) => {
    const selectVariant = error ? "error" : variant;
    
    return (
      <div className="w-full">
        <select
          className={cn(selectVariants({ variant: selectVariant, size, hasBorder, className }))}
          ref={ref}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error || helperText ? `${props.id || "select"}-helper` : undefined}
          value={props.value !== undefined ? props.value : ""}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options
            ? options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))
            : children}
        </select>
        {(error || helperText) && (
          <p
            id={`${props.id || "select"}-helper`}
            className={cn(
              "mt-1 text-sm",
              error ? "text-red-600" : "text-gray-500"
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export { Select, selectVariants };

